import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Building2, ChevronDown, ChevronUp, Download, Edit, Eye, EyeOff, Filter, Layers, LogIn, Plus, RefreshCw, RotateCcw, Save, Trash2, Truck, UserPlus, Users, X } from 'lucide-react';
import { corporateCarrierService, type CorporateCarrier, type CorporateCarrierDriver, type CorporateCarrierPayload, type CorporateCarrierSubUser, type CorporateCarrierSubUserPayload } from '../services/corporateCarrierService';
import { driverService } from '../services/driverService';
import { vehicleService } from '../services/vehicleService';
import { useAppAlert } from '../components/AppAlertProvider';
import { citiesService, type City, type District } from '../services/citiesService';
import { settingsService } from '../services/settingsService';
import { exportRowsToExcel } from '../utils/excelExport';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = (): CorporateCarrierPayload => ({
  fullName: '',
  username: '',
  email: '',
  phone: '',
  companyTitle: '',
  taxOffice: '',
  taxNumber: '',
  city: '',
  district: '',
  address: '',
  authorizedPersonName: '',
  authorizedPersonPhone: '',
  authorizedPersonEmail: '',
  password: '',
  status: 'active',
});

type CorporateColumnKey = keyof Pick<CorporateCarrier, 'fullName' | 'companyTitle' | 'username' | 'email' | 'phone' | 'taxOffice' | 'taxNumber' | 'city' | 'district' | 'authorizedPersonName' | 'authorizedPersonPhone' | 'subUserCount' | 'driverCount' | 'vehicleCount' | 'trailerCount' | 'status' | 'createdAt' | 'updatedAt'>;

const CORPORATE_COLUMNS: Array<{ key: CorporateColumnKey; label: string; groupable?: boolean }> = [
  { key: 'fullName', label: 'Firma', groupable: true },
  { key: 'companyTitle', label: 'Ticari Ünvan' },
  { key: 'subUserCount', label: 'Alt Kullanıcı' },
  { key: 'vehicleCount', label: 'Çekici Sayısı' },
  { key: 'driverCount', label: 'Sürücü Sayısı' },
  { key: 'taxOffice', label: 'Vergi Dairesi', groupable: true },
  { key: 'taxNumber', label: 'Vergi No' },
  { key: 'city', label: 'Şehir', groupable: true },
  { key: 'district', label: 'İlçe', groupable: true },
  { key: 'authorizedPersonName', label: 'Yetkili' },
  { key: 'authorizedPersonPhone', label: 'Yetkili Telefon' },
  { key: 'trailerCount', label: 'Dorse' },
  { key: 'status', label: 'Durum', groupable: true },
  { key: 'createdAt', label: 'Oluşturma Tarihi', groupable: true },
  { key: 'updatedAt', label: 'Güncelleme Tarihi', groupable: true },
];

const DEFAULT_VISIBLE_COLUMNS = CORPORATE_COLUMNS.map((column) => column.key);
const CORPORATE_COLUMN_MAP = new Map(CORPORATE_COLUMNS.map((column) => [column.key, column]));
const LEGACY_DEFAULT_VISIBLE_COLUMNS: CorporateColumnKey[] = ['fullName', 'companyTitle', 'taxOffice', 'taxNumber', 'city', 'district', 'authorizedPersonName', 'authorizedPersonPhone', 'subUserCount', 'driverCount', 'vehicleCount', 'trailerCount', 'status', 'createdAt', 'updatedAt'];
const VIEW_SETTING_KEY = 'corporate_tracking_table_view_v1';
const PRIVATE_TABS_SETTING_KEY = 'corporate_tracking_private_filter_tabs_v1';
const PUBLIC_TABS_SETTING_KEY = 'corporate_tracking_public_filter_tabs_v1';
type FilterJoin = 'and' | 'or';
type FilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'empty' | 'notEmpty' | 'dateEquals' | 'dateOnOrAfter' | 'dateOnOrBefore' | 'dateToday' | 'dateThisWeek' | 'dateThisMonth' | 'dateLastMonth';
type FilterRule = { id: string; join: FilterJoin; column: CorporateColumnKey; operator: FilterOperator; value: string };
type SavedFilterTab = { id: string; name: string; visibility: 'private' | 'public'; filters: FilterRule[]; groupBy: CorporateColumnKey | 'none'; searchInput: string; columnFilters: Record<string, string> };
const DATE_FILTER_COLUMNS = new Set<CorporateColumnKey>(['createdAt', 'updatedAt']);

type CorporateTableView = {
  visibleColumns?: CorporateColumnKey[];
  searchInput?: string;
  filters?: FilterRule[];
  columnFilters?: Record<string, string>;
  sortKey?: CorporateColumnKey | null;
  sortDirection?: 'asc' | 'desc';
  groupBy?: CorporateColumnKey | 'none';
  columnWidths?: Record<string, number>;
  pageSize?: number;
};

const sanitizeVisibleColumns = (value: unknown): CorporateColumnKey[] => {
  const validKeys = new Set(CORPORATE_COLUMNS.map((column) => column.key));
  const items = Array.isArray(value) ? value.filter((key): key is CorporateColumnKey => validKeys.has(key as CorporateColumnKey)) : [];
  if (items.length === LEGACY_DEFAULT_VISIBLE_COLUMNS.length && items.every((key, index) => key === LEGACY_DEFAULT_VISIBLE_COLUMNS[index])) return DEFAULT_VISIBLE_COLUMNS;
  return items.length > 0 ? items : DEFAULT_VISIBLE_COLUMNS;
};

const statusLabel = (status: string) => {
  if (status === 'inactive') return 'Pasif';
  if (status === 'suspended') return 'Askıda';
  if (status === 'maintenance') return 'Bakımda';
  return 'Aktif';
};

const statusClass = (status: string) => {
  if (status === 'inactive') return 'bg-slate-100 text-slate-700';
  if (status === 'suspended') return 'bg-amber-100 text-amber-700';
  if (status === 'maintenance') return 'bg-orange-100 text-orange-700';
  return 'bg-emerald-100 text-emerald-700';
};

const normalizePhone = (value: string) => String(value || '').replace(/\D/g, '').replace(/^90/, '0').slice(0, 11);

const normalizeUpper = (value: string) => String(value || '').trim().toLocaleUpperCase('tr-TR');

const formatTurkishDateTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

function Field({ label, required, hint, children, className = '' }: { label: string; required?: boolean; hint?: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 flex items-center gap-1 text-sm font-semibold text-slate-700">
        {label}{required ? <span className="text-rose-500">*</span> : null}
      </span>
      {children}
      {hint ? <span className="mt-1 block text-xs text-slate-500">{hint}</span> : null}
    </label>
  );
}

const inputClassName = 'h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';
type CarrierVehicleOption = { id: string; plate: string; trailerPlate: string; vehicleType: string };
type CarrierVehicleForm = { cekici_plaka: string; dorse_plaka: string; vehicle_type: string; brand: string; model: string; status: string };

const emptyVehicleForm = (): CarrierVehicleForm => ({
  cekici_plaka: '',
  dorse_plaka: '',
  vehicle_type: '',
  brand: '',
  model: '',
  status: 'active',
});

const emptySubUserForm = (): CorporateCarrierSubUserPayload => ({
  fullName: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  status: 'active',
});

export default function CorporateTracking() {
  const { showAlert, showConfirm } = useAppAlert();
  const { impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<CorporateCarrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<CorporateCarrier | null>(null);
  const [form, setForm] = useState<CorporateCarrierPayload>(emptyForm());
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [subUserCarrier, setSubUserCarrier] = useState<CorporateCarrier | null>(null);
  const [subUsers, setSubUsers] = useState<CorporateCarrierSubUser[]>([]);
  const [subUsersLoading, setSubUsersLoading] = useState(false);
  const [subUserSaving, setSubUserSaving] = useState(false);
  const [editingSubUser, setEditingSubUser] = useState<CorporateCarrierSubUser | null>(null);
  const [subUserForm, setSubUserForm] = useState<CorporateCarrierSubUserPayload>(emptySubUserForm());
  const [showSubUserPassword, setShowSubUserPassword] = useState(false);
  const [vehicleCarrier, setVehicleCarrier] = useState<CorporateCarrier | null>(null);
  const [carrierVehicleRows, setCarrierVehicleRows] = useState<any[]>([]);
  const [carrierVehicleRowsLoading, setCarrierVehicleRowsLoading] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any | null>(null);
  const [vehicleForm, setVehicleForm] = useState<CarrierVehicleForm>(emptyVehicleForm());
  const [driverCarrier, setDriverCarrier] = useState<CorporateCarrier | null>(null);
  const [carrierDrivers, setCarrierDrivers] = useState<CorporateCarrierDriver[]>([]);
  const [carrierDriversLoading, setCarrierDriversLoading] = useState(false);
  const [driverSaving, setDriverSaving] = useState(false);
  const [editingDriver, setEditingDriver] = useState<CorporateCarrierDriver | null>(null);
  const [driverForm, setDriverForm] = useState({ full_name: '', phone: '', email: '', tc_number: '', city: '', district: '', cekici_plaka: '', dorse_plaka: '', vehicle_type: '', license_number: '', license_class: 'E', experience_years: 0 });
  const [carrierVehicles, setCarrierVehicles] = useState<CarrierVehicleOption[]>([]);
  const [carrierVehiclesLoading, setCarrierVehiclesLoading] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<CorporateColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [privateTabs, setPrivateTabs] = useState<SavedFilterTab[]>([]);
  const [publicTabs, setPublicTabs] = useState<SavedFilterTab[]>([]);
  const [newTabName, setNewTabName] = useState('');
  const [newTabVisibility, setNewTabVisibility] = useState<'private' | 'public'>('private');
  const [savingTabs, setSavingTabs] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [sortKey, setSortKey] = useState<CorporateColumnKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<CorporateColumnKey | 'none'>('none');
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [viewLoaded, setViewLoaded] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<CorporateColumnKey | null>(null);
  const dragColumnRef = useRef<CorporateColumnKey | null>(null);
  const resizeRef = useRef<{ key: CorporateColumnKey; startX: number; startWidth: number } | null>(null);

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await corporateCarrierService.getAll();
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Corporate carriers load failed:', error);
      showAlert({ tone: 'error', title: 'Yüklenemedi', message: 'Kurumsal nakliye firmaları alınamadı.' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRows();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadView = async () => {
      try {
        const [savedView, savedPrivateTabs, savedPublicTabs] = await Promise.all([
          settingsService.getMySettingValue<CorporateTableView | null>(VIEW_SETTING_KEY, null).catch(() => null),
          settingsService.getMySettingValue<SavedFilterTab[] | null>(PRIVATE_TABS_SETTING_KEY, null).catch(() => null),
          settingsService.getSharedSettingValue<SavedFilterTab[] | null>(PUBLIC_TABS_SETTING_KEY, null).catch(() => null),
        ]);
        if (cancelled) return;
        if (savedView) {
          setVisibleColumns(sanitizeVisibleColumns(savedView.visibleColumns));
          setSearchInput(savedView.searchInput || '');
          setFilters(Array.isArray(savedView.filters) ? savedView.filters : []);
          setColumnFilters(savedView.columnFilters || {});
          setSortKey(savedView.sortKey || null);
          setSortDirection(savedView.sortDirection === 'desc' ? 'desc' : 'asc');
          setGroupBy(savedView.groupBy || 'none');
          setColumnWidths(savedView.columnWidths || {});
          setPageSize(Number(savedView.pageSize || 25));
        }
        setPrivateTabs(Array.isArray(savedPrivateTabs) ? savedPrivateTabs : []);
        setPublicTabs(Array.isArray(savedPublicTabs) ? savedPublicTabs : []);
      } finally {
        if (!cancelled) setViewLoaded(true);
      }
    };

    void loadView();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const resizing = resizeRef.current;
      if (!resizing) return;
      const nextWidth = resizing.startWidth + event.clientX - resizing.startX;
      setColumnWidth(resizing.key, nextWidth);
    };
    const handleMouseUp = () => {
      resizeRef.current = null;
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);


  useEffect(() => {
    citiesService.getAll()
      .then((response: any) => setCities(Array.isArray(response?.data) ? response.data : []))
      .catch((error) => {
        console.error('Corporate carrier cities load failed:', error);
        setCities([]);
      });
  }, []);

  useEffect(() => {
    const selectedCity = cities.find((city) => city.name === form.city);
    if (!selectedCity) {
      setDistricts([]);
      return;
    }

    let cancelled = false;
    citiesService.getDistricts(selectedCity.id)
      .then((response: any) => {
        if (!cancelled) setDistricts(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error('Corporate carrier districts load failed:', error);
        if (!cancelled) setDistricts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [cities, form.city]);

  const resolvedColumns = useMemo(
    () => visibleColumns.map((key) => CORPORATE_COLUMN_MAP.get(key)).filter((column): column is (typeof CORPORATE_COLUMNS)[number] => Boolean(column)),
    [visibleColumns]
  );

  const getColumnValue = (row: CorporateCarrier, key: CorporateColumnKey) => {
    if (key === 'status') return statusLabel(row.status);
    if (key === 'createdAt' || key === 'updatedAt') return formatTurkishDateTime(row[key] as string | null);
    return String(row[key] ?? '');
  };

  const getRawColumnValue = (row: CorporateCarrier, key: CorporateColumnKey) => String(row[key] ?? '');

  const isSameDate = (left: Date, right: Date) => left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();

  const matchesFilterRule = (row: CorporateCarrier, rule: FilterRule) => {
    const raw = getRawColumnValue(row, rule.column);
    const display = getColumnValue(row, rule.column).toLocaleLowerCase('tr-TR');
    const value = String(rule.value || '').toLocaleLowerCase('tr-TR');
    if (rule.operator === 'empty') return !raw && !display;
    if (rule.operator === 'notEmpty') return Boolean(raw || display);
    if (rule.operator === 'contains') return display.includes(value);
    if (rule.operator === 'notContains') return !display.includes(value);
    if (rule.operator === 'equals') return display === value;
    if (rule.operator === 'notEquals') return display !== value;
    if (!DATE_FILTER_COLUMNS.has(rule.column)) return true;
    const date = raw ? new Date(raw) : null;
    if (!date || Number.isNaN(date.getTime())) return false;
    const today = new Date();
    const target = rule.value ? new Date(rule.value) : null;
    if (rule.operator === 'dateEquals') return target ? isSameDate(date, target) : false;
    if (rule.operator === 'dateOnOrAfter') return target ? date >= target : false;
    if (rule.operator === 'dateOnOrBefore') return target ? date <= target : false;
    if (rule.operator === 'dateToday') return isSameDate(date, today);
    if (rule.operator === 'dateThisWeek') {
      const start = new Date(today); start.setDate(today.getDate() - ((today.getDay() + 6) % 7)); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return date >= start && date < end;
    }
    if (rule.operator === 'dateThisMonth') return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
    if (rule.operator === 'dateLastMonth') {
      const last = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return date.getFullYear() === last.getFullYear() && date.getMonth() === last.getMonth();
    }
    return true;
  };

  const matchesAdvancedFilters = (row: CorporateCarrier) => {
    if (filters.length === 0) return true;
    return filters.reduce((result, rule, index) => {
      const match = matchesFilterRule(row, rule);
      if (index === 0) return match;
      return rule.join === 'or' ? result || match : result && match;
    }, true);
  };

  const filteredRows = useMemo(() => {
    const generalQuery = searchInput.trim().toLocaleLowerCase('tr-TR');
    const activeFilters = Object.entries(columnFilters).filter(([, value]) => String(value || '').trim());
    if (!generalQuery && activeFilters.length === 0) return rows;
    return rows.filter((row) => {
      const matchesGeneral = !generalQuery || CORPORATE_COLUMNS.some((column) => getColumnValue(row, column.key).toLocaleLowerCase('tr-TR').includes(generalQuery));
      if (!matchesGeneral) return false;
      if (!matchesAdvancedFilters(row)) return false;
      return activeFilters.every(([key, value]) => {
      const needle = String(value || '').trim().toLocaleLowerCase('tr-TR');
      return getColumnValue(row, key as CorporateColumnKey).toLocaleLowerCase('tr-TR').includes(needle);
      });
    });
  }, [rows, searchInput, columnFilters, filters]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const left = getColumnValue(a, sortKey).toLocaleLowerCase('tr-TR');
      const right = getColumnValue(b, sortKey).toLocaleLowerCase('tr-TR');
      const comparison = left.localeCompare(right, 'tr', { numeric: true });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortKey, sortDirection]);

  const groupedRows = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', rows: sortedRows }];
    const groups = sortedRows.reduce((acc: Record<string, CorporateCarrier[]>, row) => {
      const label = getColumnValue(row, groupBy) || '-';
      if (!acc[label]) acc[label] = [];
      acc[label].push(row);
      return acc;
    }, {});
    return Object.entries(groups).map(([label, groupRows]) => ({ key: label, label, rows: groupRows }));
  }, [sortedRows, groupBy]);

  const effectiveItems = useMemo(() => {
    const items: Array<CorporateCarrier | { __group: true; key: string; label: string; rows: CorporateCarrier[] }> = [];
    groupedRows.forEach((group) => {
      if (groupBy !== 'none') items.push({ __group: true, key: group.key, label: group.label, rows: group.rows });
      if (!collapsedGroups[group.key]) items.push(...group.rows);
    });
    return items;
  }, [groupedRows, groupBy, collapsedGroups]);

  const totalPages = Math.max(1, Math.ceil(effectiveItems.length / pageSize));
  const paginatedItems = useMemo(() => effectiveItems.slice((page - 1) * pageSize, page * pageSize), [effectiveItems, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchInput, columnFilters, sortKey, sortDirection, groupBy, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const handleSort = (key: CorporateColumnKey) => {
    if (sortKey === key) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  const saveCurrentView = async () => {
    const view: CorporateTableView = { visibleColumns, searchInput, filters, columnFilters, sortKey, sortDirection, groupBy, columnWidths, pageSize };
    try {
      setSavingView(true);
      await settingsService.saveMySetting(VIEW_SETTING_KEY, view, { type: 'json', category: 'ui', description: 'Corporate carriers table view' });
      showAlert({ tone: 'success', title: 'Görünüm Kaydedildi', message: 'Tablo görünümü kullanıcı hesabınıza kaydedildi.' });
    } catch (error) {
      console.error('Corporate table view save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: 'Görünüm kaydedilemedi.' });
    } finally {
      setSavingView(false);
    }
  };

  const resetView = async () => {
    setColumnFilters({});
    setFilters([]);
    setSearchInput('');
    setSortKey(null);
    setSortDirection('asc');
    setGroupBy('none');
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setColumnWidths({});
    setPageSize(25);
    setPage(1);
    setCollapsedGroups({});
    try {
      await settingsService.saveMySetting(VIEW_SETTING_KEY, { visibleColumns: DEFAULT_VISIBLE_COLUMNS, searchInput: '', filters: [], columnFilters: {}, sortKey: null, sortDirection: 'asc', groupBy: 'none', columnWidths: {}, pageSize: 25 }, { type: 'json', category: 'ui', description: 'Corporate carriers table view' });
    } catch (error) {
      console.error('Corporate table view reset save failed:', error);
    }
    showAlert({ tone: 'success', title: 'Görünüm Sıfırlandı', message: 'Görünüm varsayılana döndürüldü.' });
  };

  const moveColumn = (key: CorporateColumnKey, direction: 'left' | 'right') => {
    setVisibleColumns((current) => {
      const index = current.indexOf(key);
      if (index === -1) return current;
      const targetIndex = direction === 'left' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const setColumnWidth = (key: CorporateColumnKey, width: number) => {
    const normalizedWidth = Math.max(90, Math.min(480, Number(width) || 140));
    setColumnWidths((current) => ({ ...current, [key]: normalizedWidth }));
  };

  const dropColumn = (targetKey: CorporateColumnKey) => {
    const sourceKey = draggedColumn || dragColumnRef.current;
    if (!sourceKey || sourceKey === targetKey) return;
    setVisibleColumns((current) => {
      if (!current.includes(sourceKey) || !current.includes(targetKey)) return current;
      const next = current.filter((key) => key !== sourceKey);
      const targetIndex = next.indexOf(targetKey);
      next.splice(targetIndex, 0, sourceKey);
      return next;
    });
    setDraggedColumn(null);
    dragColumnRef.current = null;
  };

  const handleResizeStart = (key: CorporateColumnKey, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = { key, startX: event.clientX, startWidth: columnWidths[key] || 160 };
  };

  const exportExcel = () => exportRowsToExcel(sortedRows as any[], `kurumsal-nakliye-firmalari-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'KurumsalFirmalar' });

  const addFilterRule = () => setFilters((current) => [...current, { id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, join: current.length === 0 ? 'and' : 'and', column: 'fullName', operator: 'contains', value: '' }]);
  const updateFilterRule = (id: string, updates: Partial<FilterRule>) => setFilters((current) => current.map((rule) => rule.id === id ? { ...rule, ...updates } : rule));
  const removeFilterRule = (id: string) => setFilters((current) => current.filter((rule) => rule.id !== id));

  const clearFiltersAndGroup = () => {
    setSearchInput('');
    setColumnFilters({});
    setFilters([]);
    setGroupBy('none');
    setCollapsedGroups({});
  };

  const persistPrivateTabs = (tabs: SavedFilterTab[]) => settingsService.saveMySetting(PRIVATE_TABS_SETTING_KEY, tabs, { type: 'json', category: 'ui', description: 'Corporate private filter tabs' });
  const persistPublicTabs = (tabs: SavedFilterTab[]) => settingsService.saveSharedSetting(PUBLIC_TABS_SETTING_KEY, tabs, { type: 'json', category: 'ui', description: 'Corporate public filter tabs' });

  const saveCurrentFiltersAsTab = async () => {
    const name = newTabName.trim();
    if (!name) {
      showAlert({ tone: 'warning', title: 'Tab Adı Gerekli', message: 'Hızlı arama adı girin.' });
      return;
    }
    const tab: SavedFilterTab = { id: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name, visibility: newTabVisibility, filters, groupBy, searchInput, columnFilters };
    try {
      setSavingTabs(true);
      if (newTabVisibility === 'public') {
        const next = [...publicTabs, tab];
        setPublicTabs(next);
        await persistPublicTabs(next);
      } else {
        const next = [...privateTabs, tab];
        setPrivateTabs(next);
        await persistPrivateTabs(next);
      }
      setNewTabName('');
      showAlert({ tone: 'success', title: 'Kaydedildi', message: 'Hızlı arama kaydedildi.' });
    } catch (error) {
      console.error('Corporate filter tab save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: 'Hızlı arama kaydedilemedi.' });
    } finally {
      setSavingTabs(false);
    }
  };

  const applyFilterTab = (tab: SavedFilterTab) => {
    setFilters(tab.filters || []);
    setGroupBy(tab.groupBy || 'none');
    setSearchInput(tab.searchInput || '');
    setColumnFilters(tab.columnFilters || {});
    setCollapsedGroups({});
  };

  const deleteFilterTab = async (tab: SavedFilterTab) => {
    if (!(await showConfirm({ title: 'Hızlı Arama Sil', message: `${tab.name} silinsin mi?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      if (tab.visibility === 'public') {
        const next = publicTabs.filter((item) => item.id !== tab.id);
        setPublicTabs(next);
        await persistPublicTabs(next);
      } else {
        const next = privateTabs.filter((item) => item.id !== tab.id);
        setPrivateTabs(next);
        await persistPrivateTabs(next);
      }
    } catch (error) {
      console.error('Corporate filter tab delete failed:', error);
      showAlert({ tone: 'error', title: 'Silinemedi', message: 'Hızlı arama silinemedi.' });
    }
  };

  const renderCell = (row: CorporateCarrier, key: CorporateColumnKey) => {
    if (key === 'fullName') return <div className="flex items-center gap-2"><span className="rounded-lg bg-indigo-100 p-1.5 text-indigo-700"><Building2 className="h-4 w-4" /></span><span className="font-semibold text-slate-900">{row.fullName || '-'}</span></div>;
    if (key === 'username') return <>@{row.username || '-'}</>;
    if (key === 'subUserCount') return <button type="button" onClick={() => void openSubUsers(row)} className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"><Users className="h-4 w-4" /> {row.subUserCount || 0} Yönet</button>;
    if (key === 'vehicleCount') return <button type="button" onClick={() => void openVehicles(row)} className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"><Truck className="h-4 w-4" /> {row.vehicleCount || 0} Yönet</button>;
    if (key === 'driverCount') return <button type="button" onClick={() => void openDrivers(row)} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"><Users className="h-4 w-4" /> {row.driverCount || 0} Yönet</button>;
    if (key === 'status') return <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(row.status)}`}>{statusLabel(row.status)}</span>;
    if (key === 'createdAt' || key === 'updatedAt') return <>{formatTurkishDateTime(row[key] as string | null) || '-'}</>;
    return <>{String(row[key] ?? '') || '-'}</>;
  };

  const getColumnStyle = (key: CorporateColumnKey) => columnWidths[key] ? { width: columnWidths[key], minWidth: columnWidths[key] } : undefined;

  const openCreate = () => {
    setEditingRow(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (row: CorporateCarrier) => {
    setEditingRow(row);
    setForm({ ...row, password: '' });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingRow(null);
    setForm(emptyForm());
  };

  const setField = <K extends keyof CorporateCarrierPayload>(key: K, value: CorporateCarrierPayload[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleCityChange = (value: string) => {
    setForm((current) => ({ ...current, city: value, district: '' }));
  };

  const save = async () => {
    if (!String(form.fullName || '').trim()) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Firma adı zorunludur.' });
      return;
    }
    if (!String(form.taxNumber || '').replace(/\D/g, '').trim()) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Vergi numarası zorunludur.' });
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        fullName: normalizeUpper(form.fullName || ''),
        companyTitle: normalizeUpper(form.companyTitle || ''),
        taxOffice: normalizeUpper(form.taxOffice || ''),
        taxNumber: String(form.taxNumber || '').replace(/\D/g, '').slice(0, 11),
        city: normalizeUpper(form.city || ''),
        district: normalizeUpper(form.district || ''),
        authorizedPersonName: normalizeUpper(form.authorizedPersonName || ''),
        authorizedPersonPhone: normalizePhone(form.authorizedPersonPhone || ''),
      };
      if (editingRow) await corporateCarrierService.update(editingRow.id, payload);
      else await corporateCarrierService.create(payload);
      setModalOpen(false);
      setEditingRow(null);
      setForm(emptyForm());
      await loadRows();
      showAlert({ tone: 'success', title: 'Kaydedildi', message: 'Kurumsal nakliye firması kaydedildi.' });
    } catch (error: any) {
      console.error('Corporate carrier save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: error?.message || 'Kurumsal nakliye firması kaydedilemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: CorporateCarrier) => {
    if (!(await showConfirm({ title: 'Firma Sil', message: `${row.fullName} silinsin mi?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await corporateCarrierService.delete(row.id);
      await loadRows();
      showAlert({ tone: 'success', title: 'Silindi', message: 'Kurumsal nakliye firması silindi.' });
    } catch (error: any) {
      console.error('Corporate carrier delete failed:', error);
      showAlert({ tone: 'error', title: 'Silinemedi', message: error?.message || 'Kurumsal nakliye firması silinemedi.' });
    }
  };

  const loadSubUsers = async (carrierId: string | number) => {
    try {
      setSubUsersLoading(true);
      const response = await corporateCarrierService.getSubUsers(carrierId);
      setSubUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Corporate carrier sub users load failed:', error);
      showAlert({ tone: 'error', title: 'Alt Kullanıcılar Alınamadı', message: 'Alt kullanıcı listesi yüklenemedi.' });
      setSubUsers([]);
    } finally {
      setSubUsersLoading(false);
    }
  };

  const openSubUsers = async (row: CorporateCarrier) => {
    setSubUserCarrier(row);
    setEditingSubUser(null);
    setSubUserForm(emptySubUserForm());
    setShowSubUserPassword(false);
    await loadSubUsers(row.id);
  };

  const closeSubUsers = () => {
    if (subUserSaving) return;
    setSubUserCarrier(null);
    setSubUsers([]);
    setEditingSubUser(null);
    setSubUserForm(emptySubUserForm());
  };

  const editSubUser = (subUser: CorporateCarrierSubUser) => {
    setEditingSubUser(subUser);
    setSubUserForm({ ...subUser, password: '' });
    setShowSubUserPassword(false);
  };

  const resetSubUserForm = () => {
    setEditingSubUser(null);
    setSubUserForm(emptySubUserForm());
    setShowSubUserPassword(false);
  };

  const saveSubUser = async () => {
    if (!subUserCarrier) return;
    if (!String(subUserForm.fullName || '').trim() || !String(subUserForm.username || '').trim() || !String(subUserForm.email || '').trim()) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Ad soyad, kullanıcı adı ve e-posta zorunludur.' });
      return;
    }
    if (!editingSubUser && !String(subUserForm.password || '').trim()) {
      showAlert({ tone: 'warning', title: 'Şifre Gerekli', message: 'Yeni alt kullanıcı için şifre zorunludur.' });
      return;
    }

    try {
      setSubUserSaving(true);
      const payload = {
        ...subUserForm,
        fullName: normalizeUpper(subUserForm.fullName || ''),
        phone: normalizePhone(subUserForm.phone || ''),
      };
      if (editingSubUser) await corporateCarrierService.updateSubUser(subUserCarrier.id, editingSubUser.id, payload);
      else await corporateCarrierService.createSubUser(subUserCarrier.id, payload);
      await loadSubUsers(subUserCarrier.id);
      await loadRows();
      resetSubUserForm();
      showAlert({ tone: 'success', title: 'Kaydedildi', message: 'Alt kullanıcı kaydedildi.' });
    } catch (error: any) {
      console.error('Corporate carrier sub user save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: error?.message || 'Alt kullanıcı kaydedilemedi.' });
    } finally {
      setSubUserSaving(false);
    }
  };

  const removeSubUser = async (subUser: CorporateCarrierSubUser) => {
    if (!subUserCarrier) return;
    if (!(await showConfirm({ title: 'Alt Kullanıcı Sil', message: `${subUser.fullName} silinsin mi?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await corporateCarrierService.deleteSubUser(subUserCarrier.id, subUser.id);
      await loadSubUsers(subUserCarrier.id);
      await loadRows();
      if (editingSubUser?.id === subUser.id) resetSubUserForm();
      showAlert({ tone: 'success', title: 'Silindi', message: 'Alt kullanıcı silindi.' });
    } catch (error: any) {
      console.error('Corporate carrier sub user delete failed:', error);
      showAlert({ tone: 'error', title: 'Silinemedi', message: error?.message || 'Alt kullanıcı silinemedi.' });
    }
  };

  const loginAsSubUser = async (subUser: CorporateCarrierSubUser) => {
    if (!(await showConfirm({ title: 'Kullanıcı Olarak Giriş Yap', message: `${subUser.fullName} hesabına geçilsin mi?`, confirmText: 'Giriş Yap', tone: 'info' }))) return;
    const response = await impersonateUser(subUser.id);
    if (!response.success) {
      showAlert({ tone: 'error', title: 'Giriş Yapılamadı', message: response.message || 'Alt kullanıcı oturumu açılamadı.' });
      return;
    }
    navigate('/driver-mobile-home');
  };

  const loadCarrierVehicleRows = async (carrierId: string | number) => {
    try {
      setCarrierVehicleRowsLoading(true);
      const response: any = await vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: String(carrierId) });
      setCarrierVehicleRows(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Corporate carrier vehicle rows load failed:', error);
      showAlert({ tone: 'error', title: 'Çekiciler Alınamadı', message: 'Firmaya bağlı çekici listesi yüklenemedi.' });
      setCarrierVehicleRows([]);
    } finally {
      setCarrierVehicleRowsLoading(false);
    }
  };

  const openVehicles = async (row: CorporateCarrier) => {
    setVehicleCarrier(row);
    setEditingVehicle(null);
    setVehicleForm(emptyVehicleForm());
    await loadCarrierVehicleRows(row.id);
  };

  const closeVehicles = () => {
    if (vehicleSaving) return;
    setVehicleCarrier(null);
    setCarrierVehicleRows([]);
    setEditingVehicle(null);
    setVehicleForm(emptyVehicleForm());
  };

  const editVehicle = (vehicle: any) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      cekici_plaka: vehicle.cekici_plaka || vehicle.plate || '',
      dorse_plaka: vehicle.dorse_plaka || vehicle.trailer_plate || vehicle.linked_trailer_plate || '',
      vehicle_type: vehicle.vehicle_type || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      status: vehicle.status || 'active',
    });
  };

  const resetVehicleForm = () => {
    setEditingVehicle(null);
    setVehicleForm(emptyVehicleForm());
  };

  const saveVehicle = async () => {
    if (!vehicleCarrier || vehicleSaving) return;
    if (!String(vehicleForm.cekici_plaka || '').trim()) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Çekici plaka zorunludur.' });
      return;
    }

    try {
      setVehicleSaving(true);
      const payload = {
        ...vehicleForm,
        cekici_plaka: normalizeUpper(vehicleForm.cekici_plaka).replace(/\s+/g, ''),
        dorse_plaka: normalizeUpper(vehicleForm.dorse_plaka).replace(/\s+/g, ''),
        vehicle_type: normalizeUpper(vehicleForm.vehicle_type),
        brand: normalizeUpper(vehicleForm.brand),
        model: normalizeUpper(vehicleForm.model),
        owner_type: 'external',
        external_transport_account_user_id: vehicleCarrier.id,
      };
      if (editingVehicle?.id) await vehicleService.updateVehicle(editingVehicle.id, payload);
      else await vehicleService.createVehicle(payload);
      await loadCarrierVehicleRows(vehicleCarrier.id);
      await loadRows();
      resetVehicleForm();
      showAlert({ tone: 'success', title: 'Kaydedildi', message: 'Çekici kaydedildi.' });
    } catch (error: any) {
      console.error('Corporate carrier vehicle save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: error?.message || 'Çekici kaydedilemedi.' });
    } finally {
      setVehicleSaving(false);
    }
  };

  const removeVehicle = async (vehicle: any) => {
    if (!vehicleCarrier) return;
    const plate = vehicle.cekici_plaka || vehicle.plate || 'Çekici';
    if (!(await showConfirm({ title: 'Çekici Sil', message: `${plate} silinsin mi?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await vehicleService.deleteVehicle(vehicle.id);
      await loadCarrierVehicleRows(vehicleCarrier.id);
      await loadRows();
      if (editingVehicle?.id === vehicle.id) resetVehicleForm();
      showAlert({ tone: 'success', title: 'Silindi', message: 'Çekici silindi.' });
    } catch (error: any) {
      console.error('Corporate carrier vehicle delete failed:', error);
      showAlert({ tone: 'error', title: 'Silinemedi', message: error?.message || 'Çekici silinemedi.' });
    }
  };

  const emptyDriverForm = () => ({ full_name: '', phone: '', email: '', tc_number: '', city: '', district: '', cekici_plaka: '', dorse_plaka: '', vehicle_type: '', license_number: '', license_class: 'E', experience_years: 0 });

  const loadCarrierDrivers = async (carrierId: string | number) => {
    try {
      setCarrierDriversLoading(true);
      const response = await corporateCarrierService.getDrivers(carrierId);
      setCarrierDrivers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Corporate carrier drivers load failed:', error);
      showAlert({ tone: 'error', title: 'Sürücüler Alınamadı', message: 'Spot sürücü listesi yüklenemedi.' });
      setCarrierDrivers([]);
    } finally {
      setCarrierDriversLoading(false);
    }
  };

  const loadCarrierVehicles = async (carrierId: string | number) => {
    try {
      setCarrierVehiclesLoading(true);
      const response: any = await vehicleService.getVehicles({ ownerType: 'external', externalTransportAccountUserId: String(carrierId), status: 'active' });
      const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setCarrierVehicles(items.map((vehicle: any) => ({
        id: String(vehicle.id),
        plate: vehicle.cekici_plaka || vehicle.plate || '',
        trailerPlate: vehicle.dorse_plaka || vehicle.linked_trailer_plate || '',
        vehicleType: vehicle.vehicle_type || '',
      })).filter((vehicle: CarrierVehicleOption) => vehicle.plate));
    } catch (error) {
      console.error('Corporate carrier vehicles load failed:', error);
      showAlert({ tone: 'error', title: 'Çekiciler Alınamadı', message: 'Firmaya bağlı çekici listesi yüklenemedi.' });
      setCarrierVehicles([]);
    } finally {
      setCarrierVehiclesLoading(false);
    }
  };

  const openDrivers = async (row: CorporateCarrier) => {
    setDriverCarrier(row);
    setEditingDriver(null);
    setDriverForm(emptyDriverForm());
    await Promise.all([loadCarrierDrivers(row.id), loadCarrierVehicles(row.id)]);
  };

  const closeDrivers = () => {
    if (driverSaving) return;
    setDriverCarrier(null);
    setCarrierDrivers([]);
    setCarrierVehicles([]);
    setEditingDriver(null);
    setDriverForm(emptyDriverForm());
  };

  const editDriver = (driver: CorporateCarrierDriver) => {
    setEditingDriver(driver);
    setDriverForm({
      full_name: driver.fullName || '',
      phone: driver.phone || '',
      email: driver.email || '',
      tc_number: driver.tcNumber || '',
      city: driver.city || '',
      district: driver.district || '',
      cekici_plaka: driver.cekiciPlaka || '',
      dorse_plaka: driver.dorsePlaka || '',
      vehicle_type: driver.vehicleType || '',
      license_number: driver.licenseNumber || '',
      license_class: driver.licenseClass || 'E',
      experience_years: Number(driver.experienceYears || 0),
    });
  };

  const saveDriver = async () => {
    if (!driverCarrier || driverSaving) return;
    if (!String(driverForm.full_name || '').trim() || !String(driverForm.phone || '').trim()) {
      showAlert({ tone: 'warning', title: 'Eksik Bilgi', message: 'Ad soyad ve telefon zorunludur.' });
      return;
    }
    try {
      setDriverSaving(true);
      const payload = {
        ...driverForm,
        full_name: normalizeUpper(driverForm.full_name),
        city: normalizeUpper(driverForm.city),
        district: normalizeUpper(driverForm.district),
        cekici_plaka: normalizeUpper(driverForm.cekici_plaka).replace(/\s+/g, ''),
        dorse_plaka: normalizeUpper(driverForm.dorse_plaka).replace(/\s+/g, ''),
        vehicle_type: normalizeUpper(driverForm.vehicle_type),
        license_class: normalizeUpper(driverForm.license_class),
        invoice_title: normalizeUpper(driverCarrier.companyTitle || driverCarrier.fullName || ''),
        tax_office: normalizeUpper(driverCarrier.taxOffice || ''),
        tax_number: String(driverCarrier.taxNumber || '').trim(),
        external_transport_account_user_id: driverCarrier.id,
      };
      if (editingDriver) await driverService.updateApplication(editingDriver.id, payload);
      else await driverService.createApplication(payload);
      await loadCarrierDrivers(driverCarrier.id);
      await loadRows();
      setEditingDriver(null);
      setDriverForm(emptyDriverForm());
      showAlert({ tone: 'success', title: 'Kaydedildi', message: 'Spot sürücü kaydedildi.' });
    } catch (error: any) {
      console.error('Corporate carrier driver save failed:', error);
      showAlert({ tone: 'error', title: 'Kaydedilemedi', message: error?.message || 'Spot sürücü kaydedilemedi.' });
    } finally {
      setDriverSaving(false);
    }
  };

  const removeDriver = async (driver: CorporateCarrierDriver) => {
    if (!driverCarrier) return;
    if (!(await showConfirm({ title: 'Spot Sürücü Sil', message: `${driver.fullName} silinsin mi?`, confirmText: 'Sil', tone: 'warning' }))) return;
    try {
      await driverService.deleteApplication(driver.id);
      await loadCarrierDrivers(driverCarrier.id);
      await loadRows();
      if (editingDriver?.id === driver.id) {
        setEditingDriver(null);
        setDriverForm(emptyDriverForm());
      }
      showAlert({ tone: 'success', title: 'Silindi', message: 'Spot sürücü silindi.' });
    } catch (error: any) {
      console.error('Corporate carrier driver delete failed:', error);
      showAlert({ tone: 'error', title: 'Silinemedi', message: error?.message || 'Spot sürücü silinemedi.' });
    }
  };

  const driverVehicleOptions = useMemo(() => {
    if (!driverForm.cekici_plaka || carrierVehicles.some((vehicle) => vehicle.plate === driverForm.cekici_plaka)) return carrierVehicles;
    return [{ id: `current-${driverForm.cekici_plaka}`, plate: driverForm.cekici_plaka, trailerPlate: driverForm.dorse_plaka, vehicleType: driverForm.vehicle_type }, ...carrierVehicles];
  }, [carrierVehicles, driverForm.cekici_plaka, driverForm.dorse_plaka, driverForm.vehicle_type]);

  const selectDriverVehicle = (plate: string) => {
    const vehicle = driverVehicleOptions.find((item) => item.plate === plate);
    setDriverForm((current) => ({
      ...current,
      cekici_plaka: plate,
      dorse_plaka: vehicle?.trailerPlate || '',
      vehicle_type: vehicle?.vehicleType ? normalizeUpper(vehicle.vehicleType) : '',
    }));
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Kurumsal Nakliye Firmaları</h1>
            <p className="mt-1 text-sm text-slate-500">Kurumsal dış nakliye firma hesaplarını yönetin.</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button type="button" onClick={openCreate} className="workiom-primary-button"><Plus className="h-5 w-5" /> Yeni Firma</button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2 sm:px-6 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-sm text-slate-500">Toplam Firma</div><div className="mt-1 text-2xl font-bold text-slate-900">{rows.length}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-sm text-slate-500">Aktif Firma</div><div className="mt-1 text-2xl font-bold text-emerald-600">{rows.filter((row) => row.status === 'active').length}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-sm text-slate-500">Toplam Çekici</div><div className="mt-1 text-2xl font-bold text-blue-600">{rows.reduce((sum, row) => sum + Number(row.vehicleCount || 0), 0)}</div></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-sm text-slate-500">Toplam Dorse</div><div className="mt-1 text-2xl font-bold text-violet-600">{rows.reduce((sum, row) => sum + Number(row.trailerCount || 0), 0)}</div></div>
      </div>

      <div className="workiom-toolbar !justify-start !pt-0">
        <div className="flex w-full flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void loadRows()} disabled={loading} className="workiom-outline-button disabled:opacity-60"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Yenile</button>
          <button type="button" onClick={() => void saveCurrentView()} disabled={!viewLoaded || savingView} className="workiom-outline-button disabled:opacity-60"><Save className="h-4 w-4" /> {savingView ? 'Kaydediliyor...' : 'Görünümü Kaydet'}</button>
            <button type="button" onClick={() => void resetView()} className="workiom-outline-button"><RotateCcw className="h-4 w-4" /> Varsayılan</button>
            <button type="button" onClick={exportExcel} className="workiom-outline-button"><Download className="h-4 w-4" /> Excel</button>
          </div>
          <div className="grid gap-3 xl:grid-cols-[minmax(260px,420px)_auto] xl:items-center xl:justify-between">
            <div className="relative">
              <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Genel arama..." className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-orange-400" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setShowFilterPanel((current) => !current)} className={`workiom-outline-button ${showFilterPanel ? '!border-blue-500 !bg-blue-50 !text-blue-700' : ''}`}><Filter className="h-4 w-4" /> Filtrele</button>
          <button type="button" onClick={() => setShowColumnFilters((current) => !current)} className={`workiom-outline-button ${showColumnFilters ? '!border-blue-500 !bg-blue-50 !text-blue-700' : ''}`}>Kolon Filtreleri</button>
          <button type="button" onClick={() => setShowColumnPanel((current) => !current)} className={`workiom-outline-button ${showColumnPanel ? '!border-orange-500 !bg-orange-50 !text-orange-700' : ''}`}>{showColumnPanel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Sütunlar</button>
          <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
            <Layers className="h-4 w-4 text-slate-500" />
            <select value={groupBy} onChange={(event) => { setGroupBy(event.target.value as CorporateColumnKey | 'none'); setCollapsedGroups({}); }} className="bg-transparent outline-none">
              <option value="none">Gruplandırma Yok</option>
              {CORPORATE_COLUMNS.filter((column) => column.groupable).map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}
            </select>
          </div>
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 outline-none">
                <option value={10}>10 kayıt</option>
                <option value={25}>25 kayıt</option>
                <option value={50}>50 kayıt</option>
                <option value={100}>100 kayıt</option>
                <option value={250}>250 kayıt</option>
              </select>
          <div className="text-sm text-slate-500">{sortedRows.length} / {rows.length} kayıt</div>
            </div>
          </div>
        </div>
      </div>

      {showFilterPanel ? (
        <div className="mx-6 mb-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700"><Filter className="h-4 w-4" /> Filtre ve Gruplandırma</div>
            <div className="flex flex-wrap items-center gap-2">
              <input value={newTabName} onChange={(event) => setNewTabName(event.target.value)} placeholder="Yeni tab adı" className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none" />
              <select value={newTabVisibility} onChange={(event) => setNewTabVisibility(event.target.value as 'private' | 'public')} className="h-9 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none"><option value="private">Private</option><option value="public">Public</option></select>
              <button type="button" onClick={() => void saveCurrentFiltersAsTab()} disabled={savingTabs} className="inline-flex items-center gap-2 rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 disabled:opacity-60"><Plus className="h-4 w-4" /> Hızlı Arama Kaydet</button>
              <button type="button" onClick={clearFiltersAndGroup} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700"><X className="h-4 w-4" /> Temizle</button>
            </div>
          </div>

          {(privateTabs.length > 0 || publicTabs.length > 0) ? (
            <div className="mb-3 flex flex-wrap gap-2">
              {[...privateTabs, ...publicTabs].map((tab) => (
                <span key={tab.id} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tab.visibility === 'public' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-blue-300 bg-blue-50 text-blue-700'}`}>
                  <button type="button" onClick={() => applyFilterTab(tab)}>{tab.name} ({tab.visibility})</button>
                  <button type="button" onClick={() => void deleteFilterTab(tab)} className="text-rose-600"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          ) : null}

          <div className="space-y-2">
            {filters.map((rule, index) => (
              <div key={rule.id} className="grid gap-2 lg:grid-cols-[90px_220px_180px_minmax(220px,1fr)_48px] lg:items-center">
                <select value={index === 0 ? 'and' : rule.join} onChange={(event) => updateFilterRule(rule.id, { join: event.target.value as FilterJoin })} disabled={index === 0} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm outline-none disabled:opacity-60"><option value="and">Ve</option><option value="or">Veya</option></select>
                <select value={rule.column} onChange={(event) => updateFilterRule(rule.id, { column: event.target.value as CorporateColumnKey, operator: 'contains', value: '' })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm outline-none">{CORPORATE_COLUMNS.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}</select>
                <select value={rule.operator} onChange={(event) => updateFilterRule(rule.id, { operator: event.target.value as FilterOperator })} className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm outline-none">
                  <option value="contains">İçinde</option><option value="notContains">İçermiyor</option><option value="equals">Eşit</option><option value="notEquals">Eşit Değil</option><option value="empty">Boş</option><option value="notEmpty">Dolu</option>
                  {DATE_FILTER_COLUMNS.has(rule.column) ? <><option value="dateEquals">Tarih =</option><option value="dateOnOrAfter">Tarih &gt;=</option><option value="dateOnOrBefore">Tarih &lt;=</option><option value="dateToday">Bugün</option><option value="dateThisWeek">Bu Hafta</option><option value="dateThisMonth">Bu Ay</option><option value="dateLastMonth">Geçen Ay</option></> : null}
                </select>
                <input value={rule.value} onChange={(event) => updateFilterRule(rule.id, { value: event.target.value })} type={DATE_FILTER_COLUMNS.has(rule.column) && ['dateEquals', 'dateOnOrAfter', 'dateOnOrBefore'].includes(rule.operator) ? 'date' : 'text'} disabled={['empty', 'notEmpty', 'dateToday', 'dateThisWeek', 'dateThisMonth', 'dateLastMonth'].includes(rule.operator)} placeholder="Filtre değeri" className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm outline-none disabled:opacity-60" />
                <button type="button" onClick={() => removeFilterRule(rule.id)} className="inline-flex h-9 items-center justify-center rounded-lg border border-rose-300 bg-rose-50 text-rose-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addFilterRule} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700"><Plus className="h-4 w-4" /> Yeni Filtre Ekle</button>
        </div>
      ) : null}

      {showColumnPanel ? (
        <div className="mx-6 mb-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 text-sm font-semibold text-slate-700">Sütun görünürlüğü, sırası ve genişliği</div>
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            {[...visibleColumns.map((key) => CORPORATE_COLUMNS.find((column) => column.key === key)).filter(Boolean), ...CORPORATE_COLUMNS.filter((column) => !visibleColumns.includes(column.key))].map((column) => {
              if (!column) return null;
              const visible = visibleColumns.includes(column.key);
              const index = visibleColumns.indexOf(column.key);
              return (
                <div
                  key={column.key}
                  draggable={visible}
                  onDragStart={() => visible && setDraggedColumn(column.key)}
                  onDragOver={(event) => visible && event.preventDefault()}
                  onDrop={() => visible && dropColumn(column.key)}
                  onDragEnd={() => setDraggedColumn(null)}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2 text-sm text-slate-700 ${draggedColumn === column.key ? 'border-blue-400 bg-blue-50' : 'border-slate-200'} ${visible ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  title={visible ? 'Sürükleyip bırakarak sütun sırasını değiştirin' : undefined}
                >
                  <label className="flex min-w-36 items-center gap-2">
                    <input type="checkbox" checked={visible} onChange={(event) => setVisibleColumns((current) => event.target.checked ? [...current, column.key] : current.filter((key) => key !== column.key))} />
                    <span>{column.label}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => moveColumn(column.key, 'left')} disabled={!visible || index <= 0} className="rounded-lg border border-slate-300 px-2 py-1 text-xs disabled:opacity-30">Sola</button>
                    <button type="button" onClick={() => moveColumn(column.key, 'right')} disabled={!visible || index === -1 || index >= visibleColumns.length - 1} className="rounded-lg border border-slate-300 px-2 py-1 text-xs disabled:opacity-30">Sağa</button>
                    <input type="number" min={90} max={480} value={columnWidths[column.key] || ''} onChange={(event) => setColumnWidth(column.key, Number(event.target.value))} placeholder="px" className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-xs" title="Sütun genişliği (px)" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mx-3 grid gap-3 md:hidden">
        {sortedRows.map((row) => (
          <div key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-slate-900">{row.fullName || '-'}</div>
                <div className="mt-1 text-xs text-slate-500">{row.companyTitle || row.authorizedPersonName || '-'}</div>
              </div>
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(row.status)}`}>{statusLabel(row.status)}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
              <div><span className="font-semibold text-slate-500">Yetkili Telefon:</span> {row.authorizedPersonPhone || '-'}</div>
              <div><span className="font-semibold text-slate-500">Şehir:</span> {row.city || '-'}</div>
              <div><span className="font-semibold text-slate-500">Alt Kullanıcı:</span> {row.subUserCount || 0}</div>
              <div><span className="font-semibold text-slate-500">Çekici:</span> {row.vehicleCount || 0}</div>
              <div><span className="font-semibold text-slate-500">Sürücü:</span> {row.driverCount || 0}</div>
              <div><span className="font-semibold text-slate-500">Güncelleme:</span> {formatTurkishDateTime(row.updatedAt as string | null) || '-'}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => void openSubUsers(row)} className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700">Alt Kullanıcı</button>
              <button type="button" onClick={() => void openVehicles(row)} className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">Çekici</button>
              <button type="button" onClick={() => void openDrivers(row)} className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">Sürücü</button>
              <button type="button" onClick={() => openEdit(row)} className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white">Düzenle</button>
            </div>
          </div>
        ))}
      </div>

      <div className="workiom-grid-wrap hidden border border-slate-200 bg-white visible-scrollbars md:block" style={{ height: 450, overflow: 'auto' }}>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-slate-500">Yükleniyor...</div>
        ) : (
          <table className="workiom-grid-table text-sm">
            <thead>
              <tr>
                <th className="workiom-grid-index text-left">#</th>
                {resolvedColumns.map((column) => (
                  <th key={column.key} className="relative text-left" style={getColumnStyle(column.key)}>
                    <div
                      draggable
                      onDragStart={(event) => { dragColumnRef.current = column.key; setDraggedColumn(column.key); event.dataTransfer.setData('text/plain', column.key); }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => { event.preventDefault(); dropColumn(column.key); }}
                      onDragEnd={() => { dragColumnRef.current = null; setDraggedColumn(null); }}
                      className="inline-flex cursor-move items-center gap-1 rounded-lg px-1 py-0.5 font-semibold text-slate-700 hover:bg-slate-100"
                      title="Sürükleyip bırakarak kolon yerini değiştirin"
                    >
                      <button type="button" onClick={() => handleSort(column.key)} className="inline-flex items-center gap-1" title="Sırala">
                        {column.label}
                        {sortKey === column.key ? (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4 text-blue-600" /> : <ChevronDown className="h-4 w-4 text-blue-600" />) : null}
                      </button>
                    </div>
                    <div onMouseDown={(event) => handleResizeStart(column.key, event)} className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-orange-400 active:bg-orange-500" title="Sütun genişliğini değiştir" />
                  </th>
                ))}
                <th className="workiom-grid-sticky-right text-left">İşlemler</th>
              </tr>
              {showColumnFilters ? (
                <tr>
                  <th className="workiom-grid-index"></th>
                  {resolvedColumns.map((column) => (
                    <th key={`filter-${column.key}`} style={getColumnStyle(column.key)}>
                      <input value={columnFilters[column.key] || ''} onChange={(event) => setColumnFilters((current) => ({ ...current, [column.key]: event.target.value }))} placeholder={`${column.label} filtre`} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none focus:border-blue-500" />
                    </th>
                  ))}
                  <th className="workiom-grid-sticky-right"></th>
                </tr>
              ) : null}
            </thead>
            <tbody>
              {paginatedItems.map((item, index) => {
                if ('__group' in item) {
                  return (
                    <tr key={`group-${item.key}`} className="cursor-pointer bg-slate-100" onClick={() => setCollapsedGroups((current) => ({ ...current, [item.key]: !current[item.key] }))}>
                      <td colSpan={resolvedColumns.length + 2} className="px-3 py-2 text-sm font-bold text-slate-700">
                        <span className="inline-flex items-center gap-2"><span className="inline-flex h-5 w-5 items-center justify-center rounded border border-slate-300 bg-white text-[11px]">{collapsedGroups[item.key] ? '+' : '-'}</span><Layers className="h-4 w-4" />{item.label} ({item.rows.length})</span>
                      </td>
                    </tr>
                  );
                }
                const row = item;
                const absoluteIndex = (page - 1) * pageSize + index + 1;
                return (
                  <tr key={row.id} onDoubleClick={() => openEdit(row)} className="cursor-pointer">
                    <td className="workiom-grid-index">{absoluteIndex}</td>
                    {resolvedColumns.map((column) => <td key={column.key} style={getColumnStyle(column.key)}>{renderCell(row, column.key)}</td>)}
                    <td className="workiom-grid-sticky-right"><div className="flex gap-2"><button type="button" onClick={() => void openSubUsers(row)} title="Alt kullanıcıları yönet" className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-indigo-700 hover:bg-indigo-100"><Users className="h-4 w-4" /></button><button type="button" onClick={() => void openVehicles(row)} title="Çekicileri yönet" className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"><Truck className="h-4 w-4" /></button><button type="button" onClick={() => void openDrivers(row)} title="Sürücüleri yönet" className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-700 hover:bg-emerald-100"><UserPlus className="h-4 w-4" /></button><button type="button" onClick={() => openEdit(row)} title="Düzenle" className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"><Edit className="h-4 w-4" /></button><button type="button" onClick={() => void remove(row)} title="Sil" className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" /></button></div></td>
                  </tr>
                );
              })}
              {sortedRows.length === 0 ? <tr><td colSpan={resolvedColumns.length + 2} className="px-4 py-8 text-center text-slate-500">Kurumsal nakliye firması bulunamadı.</td></tr> : null}
            </tbody>
          </table>
        )}
      </div>
      <div className="mx-6 mb-6 mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 md:flex-row md:items-center md:justify-between">
        <div>{effectiveItems.length === 0 ? 'Gösterilecek kayıt yok' : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, effectiveItems.length)} / ${effectiveItems.length} satır`}</div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setPage(1)} disabled={page === 1} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40">İlk</button>
          <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40">Önceki</button>
          <span>Sayfa {page} / {totalPages}</span>
          <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page === totalPages} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40">Sonraki</button>
          <button type="button" onClick={() => setPage(totalPages)} disabled={page === totalPages} className="rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-40">Son</button>
        </div>
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <div className="text-xl font-bold text-slate-900">{editingRow ? 'Kurumsal Firma Düzenle' : 'Yeni Kurumsal Firma'}</div>
                <div className="mt-1 text-sm text-slate-500">Firma bilgilerini yönetin. Giriş yapacak kişiler Alt Kullanıcılar bölümünden oluşturulur.</div>
              </div>
              <button type="button" onClick={closeModal} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-6">
              <datalist id="corporate-carrier-cities">
                {cities.map((city) => <option key={city.id} value={city.name} />)}
              </datalist>
              <datalist id="corporate-carrier-districts">
                {districts.map((district) => <option key={district.id} value={district.name} />)}
              </datalist>

              <div className="space-y-5">
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><Building2 className="h-4 w-4" /> Firma Bilgileri</div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Firma Adı" required><input value={form.fullName || ''} onChange={(event) => setField('fullName', event.target.value)} placeholder="Örn: DOĞUŞ LOJİSTİK A.Ş." className={inputClassName} /></Field>
                    <Field label="Ticari Ünvan"><input value={form.companyTitle || ''} onChange={(event) => setField('companyTitle', event.target.value)} placeholder="Fatura/ticari ünvan" className={inputClassName} /></Field>
                    <Field label="Vergi Dairesi"><input value={form.taxOffice || ''} onChange={(event) => setField('taxOffice', event.target.value)} placeholder="Örn: Büyük Mükellefler" className={inputClassName} /></Field>
                    <Field label="Vergi No" required><input value={form.taxNumber || ''} onChange={(event) => setField('taxNumber', event.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="10 veya 11 haneli vergi/TC no" className={inputClassName} /></Field>
                    <Field label="Şehir" hint="Yazmaya başlayın ve listeden il seçin."><input list="corporate-carrier-cities" value={form.city || ''} onChange={(event) => handleCityChange(event.target.value)} placeholder="İl seçin" className={inputClassName} /></Field>
                    <Field label="İlçe" hint={form.city ? 'Seçilen ile göre filtrelenir.' : 'Önce il seçin.'}><input list="corporate-carrier-districts" value={form.district || ''} onChange={(event) => setField('district', event.target.value)} placeholder="İlçe seçin" disabled={!form.city} className={`${inputClassName} disabled:bg-slate-100 disabled:text-slate-400`} /></Field>
                    <Field label="Durum"><select value={form.status || 'active'} onChange={(event) => setField('status', event.target.value as any)} className={inputClassName}><option value="active">Aktif</option><option value="inactive">Pasif</option><option value="suspended">Askıda</option></select></Field>
                    <Field label="Adres" className="md:col-span-2"><textarea value={form.address || ''} onChange={(event) => setField('address', event.target.value)} placeholder="Açık adres" rows={3} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></Field>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-500">Yetkili Kişi</div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label="Yetkili Ad Soyad"><input value={form.authorizedPersonName || ''} onChange={(event) => setField('authorizedPersonName', event.target.value)} placeholder="Ad Soyad" className={inputClassName} /></Field>
                    <Field label="Yetkili Telefon" hint="Format: 05XXXXXXXXX"><input inputMode="tel" value={form.authorizedPersonPhone || ''} onChange={(event) => setField('authorizedPersonPhone', normalizePhone(event.target.value))} placeholder="05XXXXXXXXX" className={inputClassName} /></Field>
                    <Field label="Yetkili E-posta"><input type="email" value={form.authorizedPersonEmail || ''} onChange={(event) => setField('authorizedPersonEmail', event.target.value.trim())} placeholder="yetkili@ornek.com" className={inputClassName} /></Field>
                  </div>
                </section>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
              <button type="button" onClick={closeModal} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
              <button type="button" onClick={() => void save()} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Save className="h-4 w-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      ) : null}

      {subUserCarrier ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <div className="text-xl font-bold text-slate-900">Alt Kullanıcılar</div>
                <div className="mt-1 text-sm text-slate-500">{subUserCarrier.fullName} firmasına bağlı kullanıcıları ekleyin, düzenleyin veya silin.</div>
              </div>
              <button type="button" onClick={closeSubUsers} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-auto p-6 lg:grid-cols-[1fr_420px]">
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                  <div className="font-semibold text-slate-900">Kullanıcı Listesi</div>
                  <button type="button" onClick={resetSubUserForm} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"><UserPlus className="h-4 w-4" /> Yeni Alt Kullanıcı</button>
                </div>
                <div className="max-h-[560px] overflow-auto">
                  {subUsersLoading ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Alt kullanıcılar yükleniyor...</div>
                  ) : subUsers.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Bu firmaya bağlı alt kullanıcı yok.</div>
                  ) : (
                    <table className="workiom-grid-table text-sm">
                      <thead><tr><th className="text-left">Ad Soyad</th><th className="text-left">Kullanıcı</th><th className="text-left">E-posta</th><th className="text-left">Telefon</th><th className="text-left">Durum</th><th className="text-left">İşlem</th></tr></thead>
                      <tbody>
                        {subUsers.map((subUser) => (
                          <tr key={subUser.id}>
                            <td className="font-semibold text-slate-900">{subUser.fullName || '-'}</td>
                            <td>@{subUser.username || '-'}</td>
                            <td>{subUser.email || '-'}</td>
                            <td>{subUser.phone || '-'}</td>
                            <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(subUser.status)}`}>{statusLabel(subUser.status)}</span></td>
                            <td><div className="flex gap-2"><button type="button" onClick={() => void loginAsSubUser(subUser)} title="Bu kullanıcı olarak giriş yap" className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-700 hover:bg-emerald-100"><LogIn className="h-4 w-4" /></button><button type="button" onClick={() => editSubUser(subUser)} className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"><Edit className="h-4 w-4" /></button><button type="button" onClick={() => void removeSubUser(subUser)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" /></button></div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 text-lg font-bold text-slate-900">{editingSubUser ? 'Alt Kullanıcı Düzenle' : 'Yeni Alt Kullanıcı'}</div>
                <div className="space-y-4">
                  <Field label="Ad Soyad" required><input value={subUserForm.fullName || ''} onChange={(event) => setSubUserForm((current) => ({ ...current, fullName: event.target.value }))} placeholder="Ad Soyad" className={inputClassName} /></Field>
                  <Field label="Kullanıcı Adı" required><input value={subUserForm.username || ''} onChange={(event) => setSubUserForm((current) => ({ ...current, username: event.target.value.trim() }))} placeholder="kullanici-adi" className={inputClassName} /></Field>
                  <Field label="E-posta" required><input type="email" value={subUserForm.email || ''} onChange={(event) => setSubUserForm((current) => ({ ...current, email: event.target.value.trim() }))} placeholder="kullanici@firma.com" className={inputClassName} /></Field>
                  <Field label="Telefon" hint="Format: 05XXXXXXXXX"><input inputMode="tel" value={subUserForm.phone || ''} onChange={(event) => setSubUserForm((current) => ({ ...current, phone: normalizePhone(event.target.value) }))} placeholder="05XXXXXXXXX" className={inputClassName} /></Field>
                  <Field label={editingSubUser ? 'Yeni Şifre' : 'Şifre'} required={!editingSubUser} hint={editingSubUser ? 'Boş bırakırsanız mevcut şifre değişmez.' : 'Alt kullanıcının giriş şifresi.'}>
                    <div className="relative">
                      <input type={showSubUserPassword ? 'text' : 'password'} value={subUserForm.password || ''} onChange={(event) => setSubUserForm((current) => ({ ...current, password: event.target.value }))} placeholder={editingSubUser ? 'Değiştirmek için yeni şifre' : 'Şifre girin'} className={`${inputClassName} pr-11`} autoComplete="new-password" />
                      <button type="button" onClick={() => setShowSubUserPassword((current) => !current)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-400 hover:bg-slate-100">{showSubUserPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                    </div>
                  </Field>
                  <Field label="Durum"><select value={subUserForm.status || 'active'} onChange={(event) => setSubUserForm((current) => ({ ...current, status: event.target.value as any }))} className={inputClassName}><option value="active">Aktif</option><option value="inactive">Pasif</option><option value="suspended">Askıda</option></select></Field>
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  <button type="button" onClick={resetSubUserForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Temizle</button>
                  <button type="button" onClick={() => void saveSubUser()} disabled={subUserSaving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Save className="h-4 w-4" /> Kaydet</button>
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {vehicleCarrier ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <div className="text-xl font-bold text-slate-900">Çekiciler</div>
                <div className="mt-1 text-sm text-slate-500">{vehicleCarrier.fullName} firmasına bağlı çekici ve dorse bilgilerini ekleyin, düzenleyin veya silin.</div>
              </div>
              <button type="button" onClick={closeVehicles} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-auto p-6 lg:grid-cols-[1fr_420px]">
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                  <div className="font-semibold text-slate-900">Çekici Listesi</div>
                  <button type="button" onClick={resetVehicleForm} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Yeni Çekici</button>
                </div>
                <div className="max-h-[560px] overflow-auto">
                  {carrierVehicleRowsLoading ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Çekiciler yükleniyor...</div>
                  ) : carrierVehicleRows.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Bu firmaya bağlı çekici yok.</div>
                  ) : (
                    <table className="workiom-grid-table text-sm">
                      <thead><tr><th className="text-left">Çekici</th><th className="text-left">Dorse</th><th className="text-left">Araç Tipi</th><th className="text-left">Durum</th><th className="text-left">İşlem</th></tr></thead>
                      <tbody>
                        {carrierVehicleRows.map((vehicle) => (
                          <tr key={vehicle.id}>
                            <td className="font-semibold text-slate-900">{vehicle.cekici_plaka || vehicle.plate || '-'}</td>
                            <td>{vehicle.dorse_plaka || vehicle.trailer_plate || vehicle.linked_trailer_plate || '-'}</td>
                            <td>{vehicle.vehicle_type || '-'}</td>
                            <td><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(vehicle.status || 'active')}`}>{statusLabel(vehicle.status || 'active')}</span></td>
                            <td><div className="flex gap-2"><button type="button" onClick={() => editVehicle(vehicle)} className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"><Edit className="h-4 w-4" /></button><button type="button" onClick={() => void removeVehicle(vehicle)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" /></button></div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 text-lg font-bold text-slate-900">{editingVehicle ? 'Çekici Düzenle' : 'Yeni Çekici'}</div>
                <div className="space-y-3">
                  <Field label="Çekici Plaka" required><input value={vehicleForm.cekici_plaka} onChange={(event) => setVehicleForm((current) => ({ ...current, cekici_plaka: event.target.value }))} placeholder="34ABC123" className={inputClassName} /></Field>
                  <Field label="Dorse Plaka"><input value={vehicleForm.dorse_plaka} onChange={(event) => setVehicleForm((current) => ({ ...current, dorse_plaka: event.target.value }))} placeholder="34ABC124" className={inputClassName} /></Field>
                  <Field label="Araç Tipi"><input value={vehicleForm.vehicle_type} onChange={(event) => setVehicleForm((current) => ({ ...current, vehicle_type: event.target.value }))} placeholder="TENTELİ / FRİGO / LOWBED" className={inputClassName} /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Marka"><input value={vehicleForm.brand} onChange={(event) => setVehicleForm((current) => ({ ...current, brand: event.target.value }))} className={inputClassName} /></Field>
                    <Field label="Model"><input value={vehicleForm.model} onChange={(event) => setVehicleForm((current) => ({ ...current, model: event.target.value }))} className={inputClassName} /></Field>
                  </div>
                  <Field label="Durum"><select value={vehicleForm.status} onChange={(event) => setVehicleForm((current) => ({ ...current, status: event.target.value }))} className={inputClassName}><option value="active">Aktif</option><option value="inactive">Pasif</option><option value="maintenance">Bakımda</option></select></Field>
                </div>
                <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={resetVehicleForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Temizle</button><button type="button" onClick={() => void saveVehicle()} disabled={vehicleSaving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Save className="h-4 w-4" /> Kaydet</button></div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {driverCarrier ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="flex max-h-[calc(100vh-1rem)] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <div className="text-xl font-bold text-slate-900">Spot Sürücüler</div>
                <div className="mt-1 text-sm text-slate-500">{driverCarrier.fullName} firmasına bağlı spot sürücüleri ekleyin, düzenleyin veya silin.</div>
              </div>
              <button type="button" onClick={closeDrivers} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-auto p-6 lg:grid-cols-[1fr_420px]">
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                  <div className="font-semibold text-slate-900">Sürücü Listesi</div>
                  <button type="button" onClick={() => { setEditingDriver(null); setDriverForm(emptyDriverForm()); }} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"><UserPlus className="h-4 w-4" /> Yeni Spot Sürücü</button>
                </div>
                <div className="max-h-[560px] overflow-auto">
                  {carrierDriversLoading ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Spot sürücüler yükleniyor...</div>
                  ) : carrierDrivers.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-slate-500">Bu firmaya bağlı spot sürücü yok.</div>
                  ) : (
                    <table className="workiom-grid-table text-sm">
                      <thead><tr><th>Ad Soyad</th><th>Telefon</th><th>Çekici</th><th>Dorse</th><th>İşlem</th></tr></thead>
                      <tbody>
                        {carrierDrivers.map((driver) => (
                          <tr key={driver.id}>
                            <td className="font-semibold text-slate-900">{driver.fullName || '-'}</td>
                            <td>{driver.phone || '-'}</td>
                            <td>{driver.cekiciPlaka || '-'}</td>
                            <td>{driver.dorsePlaka || '-'}</td>
                            <td><div className="flex gap-2"><button type="button" onClick={() => editDriver(driver)} className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 hover:bg-blue-100"><Edit className="h-4 w-4" /></button><button type="button" onClick={() => void removeDriver(driver)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 hover:bg-rose-100"><Trash2 className="h-4 w-4" /></button></div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 text-lg font-bold text-slate-900">{editingDriver ? 'Spot Sürücü Düzenle' : 'Yeni Spot Sürücü'}</div>
                <div className="space-y-3">
                  <Field label="Ad Soyad" required><input value={driverForm.full_name} onChange={(event) => setDriverForm((current) => ({ ...current, full_name: event.target.value }))} placeholder="Ad Soyad" className={inputClassName} /></Field>
                  <Field label="Telefon" required><input inputMode="tel" value={driverForm.phone} onChange={(event) => setDriverForm((current) => ({ ...current, phone: normalizePhone(event.target.value) }))} placeholder="05XXXXXXXXX" className={inputClassName} /></Field>
                  <Field label="E-posta"><input type="email" value={driverForm.email} onChange={(event) => setDriverForm((current) => ({ ...current, email: event.target.value.trim() }))} placeholder="surucu@ornek.com" className={inputClassName} /></Field>
                  <Field label="TC No"><input value={driverForm.tc_number} onChange={(event) => setDriverForm((current) => ({ ...current, tc_number: event.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="TC Kimlik No" className={inputClassName} /></Field>
                  <div className="grid grid-cols-2 gap-3"><Field label="İl"><input value={driverForm.city} onChange={(event) => setDriverForm((current) => ({ ...current, city: event.target.value }))} className={inputClassName} /></Field><Field label="İlçe"><input value={driverForm.district} onChange={(event) => setDriverForm((current) => ({ ...current, district: event.target.value }))} className={inputClassName} /></Field></div>
                  <Field label="Çekici Plaka" hint={carrierVehiclesLoading ? 'Çekiciler yükleniyor...' : driverVehicleOptions.length === 0 ? 'Bu firmaya bağlı aktif çekici bulunamadı.' : 'Yalnızca bu firmaya bağlı çekiciler seçilebilir.'}>
                    <select value={driverForm.cekici_plaka} onChange={(event) => selectDriverVehicle(event.target.value)} className={inputClassName} disabled={carrierVehiclesLoading}>
                      <option value="">Çekici seçin</option>
                      {driverVehicleOptions.map((vehicle) => <option key={vehicle.id} value={vehicle.plate}>{vehicle.plate}{vehicle.trailerPlate ? ` / ${vehicle.trailerPlate}` : ''}</option>)}
                    </select>
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Dorse Plaka"><input value={driverForm.dorse_plaka} readOnly className={`${inputClassName} bg-slate-100 text-slate-600`} /></Field>
                    <Field label="Araç Tipi"><input value={driverForm.vehicle_type} readOnly className={`${inputClassName} bg-slate-100 text-slate-600`} /></Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3"><Field label="Ehliyet No"><input value={driverForm.license_number} onChange={(event) => setDriverForm((current) => ({ ...current, license_number: event.target.value }))} className={inputClassName} /></Field><Field label="Ehliyet Sınıfı"><input value={driverForm.license_class} onChange={(event) => setDriverForm((current) => ({ ...current, license_class: event.target.value.toLocaleUpperCase('tr-TR') }))} className={inputClassName} /></Field></div>
                  <Field label="Deneyim (Yıl)"><input type="number" min={0} value={driverForm.experience_years} onChange={(event) => setDriverForm((current) => ({ ...current, experience_years: Number(event.target.value || 0) }))} className={inputClassName} /></Field>
                </div>
                <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => { setEditingDriver(null); setDriverForm(emptyDriverForm()); }} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Temizle</button><button type="button" onClick={() => void saveDriver()} disabled={driverSaving} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Save className="h-4 w-4" /> Kaydet</button></div>
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
